import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CalendarCheck, Check, Play } from "lucide-react";
import PageShell from "../components/PageShell";
import PremiumGate from "../components/PremiumGate";
import { usePremium } from "../context/PremiumContext";
import { PLANS } from "../data/readingPlans";
import { API } from "../api";
import { getPlanState, startPlan, togglePlanDay, planProgress } from "../lib/personal";

export default function ReadingPlans() {
  const navigate = useNavigate();
  const { isPremium } = usePremium();
  const [searchParams] = useSearchParams();
  const deepLink = searchParams.get("plan");
  const [open, setOpen] = useState(
    deepLink && PLANS.some((p) => p.id === deepLink) ? deepLink : null
  );
  const [, force] = useState(0);
  const refresh = () => force((n) => n + 1);

  const plan = PLANS.find((p) => p.id === open);

  if (!isPremium) {
    return (
      <PageShell title="Reading Plans" subtitle="Guided journeys through scripture" icon={CalendarCheck} testid="plans-page">
        <PremiumGate feature="Reading Plans" perks={["5 guided plans", "Track your progress", "Gospels, Psalms, Proverbs & more"]} />
      </PageShell>
    );
  }

  if (plan) {
    const prog = planProgress(plan.id, plan.days.length);
    const state = getPlanState()[plan.id] || { done: {} };
    return (
      <PageShell
        title={plan.title}
        subtitle={plan.subtitle}
        icon={CalendarCheck}
        accent={{ a: plan.accent, b: "#5C8A6E", c: "#2E5A66" }}
        testid="plan-detail"
      >
        <button
          onClick={() => setOpen(null)}
          className="font-ui mb-6 text-sm text-muted-foreground hover:text-foreground"
          data-testid="plan-back"
        >
          ← All plans
        </button>
        <div className="mb-6 rounded-2xl border border-border bg-background/40 p-5 backdrop-blur-md">
          <div className="mb-2 flex items-center justify-between font-ui text-sm">
            <span className="font-semibold">{prog.done} / {prog.total} days</span>
            <span className="text-primary">{prog.pct}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <div className="h-full bg-primary transition-all" style={{ width: `${prog.pct}%` }} />
          </div>
        </div>

        <div className="space-y-2">
          {plan.days.map((day, i) => {
            const done = !!state.done[i];
            return (
              <div
                key={i}
                data-testid={`plan-day-${i}`}
                className={`flex items-center gap-3 rounded-2xl border p-4 transition-colors ${
                  done ? "border-primary/40 bg-primary/5" : "border-border bg-background/40"
                }`}
              >
                <button
                  onClick={() => { startPlan(plan.id); togglePlanDay(plan.id, i); refresh(); }}
                  data-testid={`plan-check-${i}`}
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-colors ${
                    done ? "border-primary bg-primary text-primary-foreground" : "border-border"
                  }`}
                >
                  {done && <Check className="h-4 w-4" />}
                </button>
                <div className="flex-1">
                  <p className="font-ui text-xs text-muted-foreground">Day {i + 1}</p>
                  <p className="font-ui text-sm font-medium">
                    {day.map((d) => d.label).join(", ")}
                  </p>
                </div>
                <button
                  onClick={() => navigate(`/read/${day[0].book}/${day[0].chapter}?plan=${plan.id}`)}
                  data-testid={`plan-read-${i}`}
                  className="font-ui flex items-center gap-1 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground transition-transform hover:scale-105"
                >
                  <Play className="h-3.5 w-3.5" /> Read
                </button>
              </div>
            );
          })}
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      title="Reading Plans"
      subtitle="Guided journeys through scripture"
      icon={CalendarCheck}
      testid="plans-page"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {PLANS.map((p) => {
          const prog = planProgress(p.id, p.days.length);
          return (
            <button
              key={p.id}
              data-testid={`plan-${p.id}`}
              onClick={() => setOpen(p.id)}
              className="group relative overflow-hidden rounded-3xl border border-border bg-background/40 p-6 text-left backdrop-blur-md transition-all hover:border-primary/50"
            >
              <img
                src={`${API}/plan/image/${p.id}`}
                alt=""
                aria-hidden="true"
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-70 transition-opacity duration-500 group-hover:opacity-85"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background/90 via-background/55 to-background/35" />
              <div className="relative z-10 [filter:drop-shadow(0_1px_6px_rgba(0,0,0,0.55))]">
                <div className="mb-4 h-1.5 w-12 rounded-full" style={{ background: p.accent }} />
                <h3 className="font-serif-display text-2xl tracking-tight">{p.title}</h3>
                <p className="font-ui mt-1 text-sm text-muted-foreground">{p.subtitle}</p>
                <p className="font-ui mt-3 text-sm leading-relaxed text-foreground/80">{p.blurb}</p>
                <div className="mt-5 flex items-center justify-between font-ui text-xs">
                  <span className="text-muted-foreground">{p.days.length} days</span>
                  {prog.started ? (
                    <span className="font-semibold text-primary">{prog.pct}% complete</span>
                  ) : (
                    <span className="font-semibold text-primary group-hover:underline">Start plan →</span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </PageShell>
  );
}
